import React, { useEffect, useState, useCallback, useRef, memo } from "react";
import {
  Box,
  TextField,
  IconButton,
  Grid,
  Button,
  InputAdornment,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import { Search as SearchIcon, Download as DownloadIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { exportOrdersReport, getAllReports } from "../../service/orderService";
import Color from "../../components/Color";
import { useSnackbar } from "../../App";
import { getAllBranchLists } from "../../service/branchService";
import CountUp from "react-countup";

import {
  ShoppingCart as TotalOrdersIcon,
  Cake as CakeOrdersIcon,
  CurrencyRupee as TotalAmountIcon,
  CheckCircle as ReceivedAmountIcon,
  Pending as PendingAmountIcon,
  DoneAll as CompleteOrdersIcon,
  Cancel as CancelledOrdersIcon,
  CookieOutlined
} from "@mui/icons-material";

const cardGradients = [
  "linear-gradient(135deg, #7C0BCC 0%, #4B0082 100%)",
  "linear-gradient(135deg, #FF6F61 0%, #FF3D00 100%)",
  "linear-gradient(135deg, #2196F3 0%, #1565C0 100%)",
  "linear-gradient(135deg, #FBC02D 0%, #F57F17 100%)",
  "linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)",
  "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
  "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)",
  "linear-gradient(135deg, #E91E63 0%, #C2185B 100%)",
];

const MemoizedCard = memo(({ stat, index }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box flex="1 1 auto" minWidth={0}>
      <Card
        sx={{
          width: "100%",
          height: { xs: 80, sm: 100, md:100, lg:80 },
          borderRadius: 2,
          boxShadow: "none",
          background: cardGradients[index % cardGradients.length],
          color: "white",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <CardContent sx={{ p: 2, flex: 1 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography
                variant="body2"
                sx={{
                 fontSize: { xs: "0.70rem", sm: "0.75rem", md: "0.8rem", lg: ".85rem" },
                  mt: { xs: 0.5, sm: 0.8 },
                  lineHeight: 1.2,
                }}
              >
                {stat.title}
              </Typography>
              <Typography
                variant="h5"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: ".80rem", sm: ".90rem", md: "1rem", lg: "1rem" },
                  mt: { xs: 0.5, sm: 0.8 },
                }}
              >
                {stat.prefix || ""}
                <CountUp end={parseFloat(stat.value) || 0} duration={1.5} decimals={stat.prefix ? 2 : 0} />
              </Typography>
            </Box>
            <Avatar
              sx={{
                bgcolor: "white",
                width: {  xs: 30, sm: 34, md: 38, lg:38 },
                height: { xs: 30, sm: 34, md: 38, lg:38 },
              }}
            >
              {stat.icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
});

const Reports = () => {
  const { showSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [dateErrors, setDateErrors] = useState({ order_date_from: "", order_date_to: "" });
  const [summary, setSummary] = useState({
    total_orders: 0,
    total_cake_orders: 0,
    total_sweet_orders: 0,
    completed_orders: 0,
    cancelled_orders: 0,
    total_sales: 0,
    pending_amount: 0,
    received_amount: 0,
  });
  const [branches, setBranches] = useState([]);
  const abortControllerRef = useRef(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const role = localStorage.getItem("role") || "2";
  const branchId = localStorage.getItem("branch") || "";
  const branchName = localStorage.getItem("branch_name") || "Your Branch";

  const statusOptions = [
    { value: "order_placed", label: "Order Placed" },
    { value: "preparing", label: "Preparing" },
    { value: "ready_for_delivery", label: "Ready for Delivery" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    // { value: "request_for_delete", label: "Request for Delete" },
    // { value: "delete_rejected", label: "Delete Rejected" },
    { value: "request_for_cancel", label: "Cancel Requested" },
    { value: "cancel_request_rejected", label: "Cancel Request Rejected" },
    { value: "refunded", label: "Refunded" },
    { value: "partially_refunded", label: "Partially Refunded" }
  ];

  const categoryOptions = [
    { value: "cake", label: "Cake Order" },
    { value: "sweet", label: "Sweet Order" },
  ];

  const initialFilterState = {
    order_number: "",
    status: "",
    order_type: "",
    payment_status: "",
    customer_id: "",
    customer_name: "",
    branch_id: role === "2" ? branchId : "",
    branch_name: role === "2" ? branchName : "",
    order_date_from: null,
    order_date_to: null,
    total_amount_min: "",
    total_amount_max: "",
    notes: "",
    sort_order: "",
    order_category: "",
  };

  const [filters, setFilters] = useState(initialFilterState);

  const cardData = [
    { title: "Total Orders", value: summary.total_orders, icon: <TotalOrdersIcon sx={{ color: Color.primary.main }} /> },
    { title: "Total Cake Orders", value: summary.total_cake_orders, icon: <CakeOrdersIcon sx={{ color: Color.primary.main }} /> },
    { title: "Total Sweet Orders", value: summary.total_sweet_orders, icon: <CookieOutlined sx={{ color: Color.primary.main }} /> },
    { title: "Total Sales", value: summary.total_sales, icon: <TotalAmountIcon sx={{ color: Color.primary.main }} />, prefix: "₹" },
    { title: "Received Amount", value: summary.received_amount, icon: <ReceivedAmountIcon sx={{ color: Color.primary.main }} />, prefix: "₹" },
    { title: "Pending Amount", value: summary.pending_amount, icon: <PendingAmountIcon sx={{ color: Color.primary.main }} />, prefix: "₹" },
    { title: "Complete Orders", value: summary.completed_orders, icon: <CompleteOrdersIcon sx={{ color: Color.primary.main }} /> },
    { title: "Cancelled Orders", value: summary.cancelled_orders, icon: <CancelledOrdersIcon sx={{ color: Color.primary.main }} /> },
  ];

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const res = await getAllBranchLists();
        if (!res || !res.data) throw new Error("No data returned from branch API");
        const branchData = res.data.data || res.data.branches || res.data || [];
        setBranches(Array.isArray(branchData) ? branchData : []);
      } catch (error) {
        console.error("Error fetching branches:", error.message || error);
        showSnackbar("Failed to fetch branches!", "error");
        setBranches([]);
      }
    };
    fetchBranches();
  }, [showSnackbar]);

  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateDates = () => {
    const errors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filters.order_date_from && filters.order_date_from > today) {
      errors.order_date_from = "Order Date cannot be in the future";
    }
    if (filters.order_date_to && filters.order_date_to > today) {
      errors.order_date_to = "Delivery Date cannot be in the future";
    }
    if (filters.order_date_from && filters.order_date_to && filters.order_date_from > filters.order_date_to) {
      errors.order_date_to = "To Date must be on or after From Date";
    }
    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = ({ target: { name, value } }) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (name === "customer_name") setSearch(value);
    if (name === "order_date_from" || name === "order_date_to") {
      setDateErrors((prev) => ({ ...prev, [name]: "" }));
    }
    if (name === "branch_id") {
      setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
      fetchOrders();
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!validateDates()) {
      showSnackbar("Please fix date errors before searching.", "error");
      return;
    }
    setFiltersApplied(true);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    fetchOrders();
  };

  const handleReset = () => {
    setFilters({
      ...initialFilterState,
      branch_id: role === "2" ? branchId : "",
      branch_name: role === "2" ? branchName : "",
    });
    setSearch("");
    setDebouncedSearch("");
    setDateErrors({ order_date_from: "", order_date_to: "" });
    setPaginationModel({ page: 0, pageSize: 10 });
    setFiltersApplied(false);
    fetchOrders();
  };

  const handleClear = () => {
    setSearch("");
    setDebouncedSearch("");
    setFilters((prev) => ({
      ...prev,
      customer_name: "",
      branch_id: role === "2" ? branchId : "",
      branch_name: role === "2" ? branchName : "",
    }));
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
    fetchOrders();
  };

  const fetchOrders = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();

    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const searchPayload = {
        ...filters,
        search: debouncedSearch,
        order_date_from: formatDate(filters.order_date_from),
        order_date_to: formatDate(filters.order_date_to),
        sort_field: sortModel.length > 0 ? sortModel[0].field : "delivery_date",
        sort_order: sortModel.length > 0 ? sortModel[0].sort : "desc",
        branch_id: filters.branch_id,
      };

      if (!searchPayload.branch_id) delete searchPayload.branch_id;

      const res = await getAllReports(
        { ...searchPayload, page, limit },
        {
          signal: abortControllerRef.current.signal,
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (res?.data?.summary) {
        setSummary({
          total_orders: res.data.summary.total_orders || 0,
          total_cake_orders: res.data.summary.total_cake_orders || 0,
          total_sweet_orders: res.data.summary.total_sweet_orders || 0,
          completed_orders: res.data.summary.completed_orders || 0,
          cancelled_orders: res.data.summary.cancelled_orders || 0,
          total_sales: res.data.summary.total_sales || 0,
          received_amount: res.data.summary.received_amount || 0,
          pending_amount: res.data.summary.pending_amount || 0,
        });
      } else {
        setSummary({
          total_orders: 0, total_cake_orders: 0, completed_orders: 0, total_sweet_orders: 0,
          cancelled_orders: 0, total_sales: 0, received_amount: 0, pending_amount: 0,
        });
      }

      const data = res?.data?.data;
      if (Array.isArray(data?.data)) {
        const normalizedRows = data.data.map((row) => ({
          ...row,
          customerName: row.customer?.name || row.customer_name || "",
          branchName: row.branch?.name || row.branch_name || "",
          order_date: row.order_date ? row.order_date.substring(0, 10) : "",
          delivery_date: row.delivery_date ? row.delivery_date.substring(0, 10) : "",
        }));
        setRows(normalizedRows);
        setRowCount(data.total || 0);
      } else {
        setRows([]);
        setRowCount(0);
        showSnackbar("No orders found or unexpected data format!", "info");
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching orders:", error);
        showSnackbar(error?.response?.data?.message || "Failed to fetch orders!", "error");
        setRows([]);
        setRowCount(0);
      }
    } finally {
      setLoading(false);
    }
  }, [filters, paginationModel, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchOrders();
    return () => {
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, [fetchOrders]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const handleDownload = async () => {
    if (!filters.order_date_from || !filters.order_date_to) {
      showSnackbar("Please select From and To dates for download.", "warning");
      return;
    }

    const from = formatDate(filters.order_date_from);
    const to = formatDate(filters.order_date_to);

    const params = {
      from_date: from,
      to_date: to,
      branch_id: filters.branch_id || "",
      token: localStorage.getItem("token"),
    };

    try {
      const response = await exportOrdersReport(params, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const fileName = `orders-report-${from}_to_${to}.csv`;
      link.setAttribute('download', fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSnackbar("Report downloaded successfully!", "success");
    } catch (error) {
      console.error("Download failed:", error);
      showSnackbar("Failed to download report. Please try again.", "error");
    }
  };

  const columns = [
    {
      field: "order_number",
      headerName: "Order No",
      flex: 1,
      minWidth: isMobile ? 100 : 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        let val = params.value || "";
        if (!val) return "-";
        
        // Remove 4-digit year segment (like -2026-) if present
        const parts = val.split("-");
        if (parts.length >= 3) {
          const secondToLast = parts[parts.length - 2];
          if (/^\d{4}$/.test(secondToLast)) {
            parts.splice(parts.length - 2, 1);
            val = parts.join("-");
          }
        }

        if (val.length <= 4) {
          return (
            <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
              <span style={{ fontWeight: 800, color: "#000000", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{val}</span>
            </div>
          );
        }
        const prefix = val.slice(0, -4);
        const lastFour = val.slice(-4);
        return (
          <div style={{ display: "flex", alignItems: "center", height: "100%", flexWrap: "nowrap" }}>
            <span style={{ color: "#666666", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{prefix}</span>
            <span style={{ fontWeight: 800, color: "#000000", fontSize: isMobile ? "0.75rem" : "0.90rem", whiteSpace: "nowrap" }}>{lastFour}</span>
          </div>
        );
      }
    },
    // { field: "order_type", headerName: "Order Type", flex: 1, minWidth: isMobile ? 100 : 130, headerAlign: "center", align: "center" },
    // {
    //   field: "order_category",
    //   headerName: "Order Category",
    //   flex: 1,
    //   minWidth: isMobile ? 100 : 130,
    //   headerAlign: "center",
    //   align: "center",
    //   renderCell: (params) => {
    //     const category = params.value;
    //     let displayText = "";
    //     let textColor = "#000";

    //     if (category === "cake") {
    //       displayText = "Cake Order";
    //       textColor = Color.primary.main;
    //     } else if (category === "sweet") {
    //       displayText = "Sweet Order";
    //       textColor = Color.secondary.main;
    //     }

    //     return (
    //       <Box
    //         sx={{
    //           display: "flex",
    //           justifyContent: "center",
    //           alignItems: "center",
    //           width: "100%",
    //           height: "100%",
    //         }}
    //       >
    //         <Typography
    //           sx={{
    //             color: textColor,
    //             fontSize: isMobile ? "0.75rem" : "0.90rem",
    //             fontWeight: 900,
    //             textTransform: "capitalize",
    //             lineHeight: "normal",
    //             textAlign: "center",
    //           }}
    //         >
    //           {displayText || "-"}
    //         </Typography>
    //       </Box>
    //     );
    //   },
    // },
    { field: "customer_name", headerName: "Customer Name", flex: 1, minWidth: isMobile ? 120 : 150, headerAlign: "left", align: "left" },
    ...(role === "1" ? [{ field: "branchName", headerName: "Branch", flex: 1, minWidth: isMobile ? 120 : 150, headerAlign: "left", align: "left" }] : []),
    {
      field: "order_date",
      headerName: "Order Date",
      flex: 1,
      minWidth: isMobile ? 120 : 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const val = params.value;
        if (!val) return "-";
        const dateOnly = val.substring(0, 10);
        const parts = dateOnly.split("-");
        const formattedDate = parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : dateOnly;
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.90rem",
                lineHeight: "normal",
                textAlign: "left",
              }}
            >
              {formattedDate}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "delivery_date",
      headerName: "Delivery Date",
      flex: 1,
      minWidth: isMobile ? 120 : 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const val = params.value;
        if (!val) return "-";
        const dateOnly = val.substring(0, 10);
        const parts = dateOnly.split("-");
        const formattedDate = parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : val;
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              width: "100%",
              height: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: isMobile ? "0.75rem" : "0.90rem",
                lineHeight: "normal",
                textAlign: "left",
              }}
            >
              {formattedDate}
            </Typography>
          </Box>
        );
      },
    },
    { field: "total_amount", headerName: "Total Amount", flex: 0.8, minWidth: isMobile ? 80 : 120, headerAlign: "left", align: "left" },
    { field: "paid_amount", headerName: "Paid Amount", flex: 0.8, minWidth: isMobile ? 80 : 120, headerAlign: "left", align: "left" },
    {
      field: "payment_status",
      headerName: "Payment Status",
      flex: 1,
      minWidth: isMobile ? 140 : 150,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const status = (params.value || "unpaid").toLowerCase();
        const label = status.charAt(0).toUpperCase() + status.slice(1);

        const badgeColors = {
          paid:    "#2e7d32",
          full:    "#2e7d32",
          partial: "#e65100",
          advance: "#f57c00",
          unpaid:  "#1976d2",
          pending: "#d32f2f",
        };

        const bg = badgeColors[status] || "#757575";

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip
              label={label.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: bg,
                color: "#fff",
                fontWeight: 700,
                fontSize: isMobile ? "0.6rem" : "0.68rem",
                borderRadius: "8px",
                height: "24px",
                letterSpacing: "0.3px",
                boxShadow: `0 2px 6px ${bg}55`,
                "& .MuiChip-label": {
                  px: 1.2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  paddingTop: "4px",
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "status",
      headerName: "Order Status",
      flex: 1,
      minWidth: isMobile ? 140 : 140,
      headerAlign: "left",
      align: "left",
      renderCell: (params) => {
        const status = params.value || "order_placed";
        const label = statusOptions.find((opt) => opt.value === status)?.label || status.replace(/_/g, " ");

        const badgeColors = {
          order_placed:            "#1976d2",
          preparing:               "#7c0bcc",
          ready_for_delivery:     "#e65100",
          completed:              "#2e7d32",
          cancelled:              "#d32f2f",
          // request_for_delete:     "#f57c00",
          // delete_rejected:        "#b71c1c",
          // request_for_cancel:     "#f57c00",
          // cancel_request_rejected: "#b71c1c",
          refunded:                "#9c27b0",
          partially_refunded:      "#7b1fa2",
        };

        const bg = badgeColors[status] || "#757575";

        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Chip
              label={label.toUpperCase()}
              size="small"
              sx={{
                backgroundColor: bg,
                color: "#fff",
                fontWeight: 700,
                fontSize: isMobile ? "0.6rem" : "0.68rem",
                borderRadius: "8px",
                height: "24px",
                letterSpacing: "0.3px",
                boxShadow: `0 2px 6px ${bg}55`,
                "& .MuiChip-label": {
                  px: 1.2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  lineHeight: 1,
                  paddingTop: "2px",
                },
              }}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1, md: 6 }} sx={{ width: "100%", padding: isMobile ? 1 : 0 }}>
      {/* Cards */}
      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "repeat(2, 1fr)",
          sm: "repeat(2, 1fr)",
          md: "repeat(4, 1fr)",
          lg: "repeat(4, 1fr)",
        }}
        gap={2}
        sx={{ width: "100%", mt: 2 }}
      >
        {cardData.map((card, index) => (
          <MemoizedCard key={card.title} stat={card} index={index} />
        ))}
      </Box>

      {/* Search + Filters + Export */}
      <Box
        sx={{
          mt: 4,
          mb: 2,
          p: 2,
          border: "1px solid lightgray",
          borderRadius: 2,
          backgroundColor: "#fafafa",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={12} md={role === "1" ? 12 : 12} lg={role === "1" ? 12 : 12}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by Order No, Customer, Branch, Date, Status or Amount..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "gray", fontSize: "1.2rem" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: 40,
                  fontSize: "0.85rem",
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
              }}
            />
          </Grid>

          {role === "1" && (
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <FormControl size="small" fullWidth>
                <Select
                  name="branch_id"
                  value={filters.branch_id}
                  onChange={handleChange}
                  displayEmpty
                  sx={{ height: 37, fontSize: "0.80rem" }}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  {branches.map((b) => (
                    <MenuItem key={b.id} value={b.id} sx={{ fontSize: "0.80rem" }}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={role === "1" ? 3 : 4} lg={role === "1" ? 3 : 4}>
            <FormControl size="small" fullWidth>
              <Select
                name="status"
                value={filters.status}
                onChange={handleChange}
                displayEmpty
                sx={{ height: 37, fontSize: "0.80rem" }}
              >
                <MenuItem value="" disabled>Select Status</MenuItem>
                {statusOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value} sx={{ fontSize: "0.80rem" }}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={role === "1" ? 3 : 4} lg={role === "1" ? 3 : 4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={filters.order_date_from}
                onChange={(date) => setFilters(prev => ({ ...prev, order_date_from: date }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "Report From Date",
                    error: !!dateErrors.order_date_from,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        height: 37,
                        fontSize: "0.80rem",
                        paddingRight: "4px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 4px 8px 8px",
                      },
                      "& .MuiIconButton-root": {
                        padding: "4px",
                        marginRight: "-2px",
                      }
                    },
                  },
                }}
                format="yyyy-MM-dd"
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={role === "1" ? 3 : 4} lg={role === "1" ? 3 : 4}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={filters.order_date_to}
                onChange={(date) => setFilters(prev => ({ ...prev, order_date_to: date }))}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    placeholder: "Report To Date",
                    error: !!dateErrors.order_date_to,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        height: 37,
                        fontSize: "0.80rem",
                        paddingRight: "4px",
                      },
                      "& .MuiInputBase-input": {
                        padding: "8px 4px 8px 8px",
                      },
                      "& .MuiIconButton-root": {
                        padding: "4px",
                        marginRight: "-2px",
                      }
                    },
                  },
                }}
                format="yyyy-MM-dd"
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} sm={6} md={role === "1" ? 6 : 12} lg={role === "1" ? 6 : 12}>
            <Button
              onClick={handleReset}
              size="small"
              fullWidth
              sx={{
                height: "37px !important",
                border: `1px solid ${Color.secondary.main}`,
                color: Color.secondary.main,
                fontSize: "0.80rem",
                textTransform: "none",
              }}
            >
              Reset
            </Button>
          </Grid>

          {role === "1" && (
            <Grid item xs={12} sm={6} md={6} lg={6}>
              <Button
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
                fullWidth
                sx={{
                  background: Color.primary.main,
                  color: "white",
                  height: "37px !important",
                  fontSize: "0.80rem",
                  textTransform: "none",
                  "&:hover": { background: Color.primary.dark },
                }}
              >
                Download
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* DataGrid */}
      <Box sx={{ width: "100%", mt: 2 }}>
        {rows.length === 0 && !loading && (
          <Box sx={{ textAlign: "center", p: 2 }}>
            <Typography sx={{ fontSize: isMobile ? "0.9rem" : "1rem", color: "text.secondary" }}>
              No orders found for the search term or filters.
            </Typography>
          </Box>
        )}
        <DataGrid
          rows={rows}
          columns={columns}
          rowHeight={isMobile ? 48 : 56}
          loading={loading}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          disableColumnMenu
              disableColumnSorting
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": { fontSize: isMobile ? "14px" : "16px", fontWeight: 600, px: 1 },
            "& .MuiDataGrid-cell": { fontSize: isMobile ? "12px" : "14px", fontWeight: 400, px: 2 },
            "& .MuiDataGrid-row:nth-of-type(even)": { backgroundColor: "#fff" },
            "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "#f4f4f4" },
            "& .MuiLinearProgress-bar": { backgroundColor: Color.primary.main },
            overflowX: isMobile ? "hidden" : "auto",
          }}
        />
      </Box>

    </Box>
  );
};

export default Reports;



//Dummy UI

// import React, { useState, useMemo, useEffect } from "react";
// import {
//   Box,
//   Card,
//   CardContent,
//   Typography,
//   Avatar,
//   Button,
//   FormControl,
//   Select,
//   MenuItem,
//   TextField,
//   InputAdornment,
//   IconButton,
//   Paper,
//   Grid,
// } from "@mui/material";
// import {
//   Search as SearchIcon,
//   Download as DownloadIcon,
//   Close as CloseIcon,
//   CurrencyRupee as CurrencyRupeeIcon,
//   ShoppingCart as ShoppingCartIcon,
//   Pending as PendingIcon,
//   CheckCircle as CheckCircleIcon,
// } from "@mui/icons-material";
// import { DataGrid } from "@mui/x-data-grid";
// import Color from "../../components/Color";
// import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import { DateRangePicker } from "react-date-range";
// import CountUp from "react-countup";
// import {
//   LineChart,
//   Line,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   PieChart,
//   Pie,
//   Cell,
//   ResponsiveContainer,
// } from "recharts";
// import "react-date-range/dist/styles.css";
// import "react-date-range/dist/theme/default.css";
// import {
//   format,
//   startOfWeek,
//   endOfWeek,
//   startOfMonth,
//   endOfMonth,
//   startOfDay,
//   endOfDay,
//   isWithinInterval,
//   subDays,
//   isValid,
// } from "date-fns";

// const safeDate = (date) => {
//   if (!date) return new Date();
//   const d = new Date(date);
//   return isValid(d) ? d : new Date();
// };

// // ONLY SWEETS MENU
// const menuItems = [
//   { name: "Donut (Chocolate)", price: 35, veg: true, category: "sweet" },
//   { name: "Cream Roll", price: 30, veg: true, category: "sweet" },
//   { name: "Gulab Jamun (2 pcs)", price: 60, veg: true, category: "sweet" },
//   { name: "Rasmalai", price: 80, veg: true, category: "sweet" },
//   { name: "Jalebi", price: 50, veg: true, category: "sweet" },
//   { name: "Ice Cream Scoop", price: 70, veg: true, category: "sweet" },
//   { name: "Ladoo", price: 90, veg: true, category: "sweet" },
//   { name: "Barfi", price: 120, veg: true, category: "sweet" },
//   { name: "Rasgulla", price: 65, veg: true, category: "sweet" },
//   { name: "Kaju Katli", price: 150, veg: true, category: "sweet" },
//   { name: "Milk Cake", price: 110, veg: true, category: "sweet" },
//   { name: "Peda", price: 85, veg: true, category: "sweet" },
//   { name: "Halwa (Carrot)", price: 95, veg: true, category: "sweet" },
//   { name: "Soan Papdi", price: 75, veg: true, category: "sweet" },
//   { name: "Mysore Pak", price: 130, veg: true, category: "sweet" },
// ];

// const branches = ["Mudaliarpet", "Ariankuppam", "Pas-lam-lon", "Da Nine"];

// // GENERATE ORDERS
// const generateOrders = () => {
//   const orders = [];
//   for (let i = 1; i <= 280; i++) {
//     const daysAgo = Math.floor(Math.random() * 90);
//     const orderDate = format(subDays(new Date(), daysAgo), "yyyy-MM-dd");
//     const itemCount = Math.floor(Math.random() * 5) + 1;
//     const items = [];
//     let total = 0;
//     for (let j = 0; j < itemCount; j++) {
//       const item = menuItems[Math.floor(Math.random() * menuItems.length)];
//       const qty = Math.floor(Math.random() * 10) + 1;
//       items.push({ name: item.name, qty, price: item.price });
//       total += qty * item.price;
//     }
//     orders.push({
//       id: i,
//       order_number: `SWEET-${1000 + i}`,
//       customer_name: ["Ravi", "Priya", "Arun", "Meera", "Suresh", "Anita", "Kavya", "Vikram"][Math.floor(Math.random() * 8)],
//       branch_name: branches[Math.floor(Math.random() * branches.length)],
//       order_date: orderDate,
//       total_amount: total,
//       items,
//       status: ["completed", "ready_for_delivery", "order_placed"][Math.floor(Math.random() * 3)],
//     });
//   }
//   return orders;
// };

// const rawOrders = generateOrders();

// const monthlySalesData = [
//   { month: "Jan", sales: 185000, orders: 1200 },
//   { month: "Feb", sales: 228000, orders: 1450 },
//   { month: "Mar", sales: 312000, orders: 1800 },
//   { month: "Apr", sales: 398000, orders: 2200 },
//   { month: "May", sales: 485000, orders: 2600 },
//   { month: "Jun", sales: 572000, orders: 3100 },
//   { month: "Jul", sales: 658000, orders: 3600 },
//   { month: "Aug", sales: 742000, orders: 4100 },
//   { month: "Sep", sales: 835000, orders: 4600 },
//   { month: "Oct", sales: 958000, orders: 5200 },
//   { month: "Nov", sales: 1120000, orders: 6100 },
// ];

// const COLORS = ["#7C0BCC", "#FF5252", "#FF9800", "#2196F3", "#4CAF50", "#00BCD4"];

// // KPI CARD
// const MemoizedCard = ({ title, value, icon, prefix = "", gradient }) => (
//   <Card sx={{ background: gradient, borderRadius: 4, color: "white", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", flex: "1 1 280px", minWidth: 280 }}>
//     <CardContent sx={{ p: 3 }}>
//       <Box display="flex" justifyContent="space-between" alignItems="center">
//         <Box>
//           <Typography variant="subtitle2" sx={{ opacity: 0.9 }}>{title}</Typography>
//           <Typography variant="h4" fontWeight="bold" sx={{ mt: 1 }}>
//             {prefix}
//             <CountUp end={value || 0} duration={2.5} decimals={prefix ? 2 : 0} separator="," />
//           </Typography>
//         </Box>
//         <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)", width: 60, height: 60 }}>{icon}</Avatar>
//       </Box>
//     </CardContent>
//   </Card>
// );

// // TOP SELLING SWEETS - COMPACT & RESPONSIVE
// const TopProductsCard = ({ products }) => {
//   const sweetProducts = products
//     .sort((a, b) => b.sales - a.sales)
//     .slice(0, 10);

//   const cakeIcon = (
//     <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
//       <path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8" />
//       <path d="M4 16s.5-1 1.5-1.5S7 16 8 16s1.5-1 2.5-1.5S12 16 13 16s1.5-1 2.5-1.5S17 16 18 16s1.5-1 2.5-1.5S22 16 22 16" />
//       <path d="M12 3v5" />
//       <path d="M9 5s.5-2 3-2 3 2 3 2" />
//     </svg>
//   );

//   return (
//     <Card sx={{ borderRadius: 4, height: "100%", boxShadow: "0 10px 30px rgba(0,0,0,0.12)", width:'100%',  }}>
//       <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
//         <Typography variant="h6" fontWeight="bold" gutterBottom color= {Color.text.primary} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
//           Top Selling Products
//         </Typography>
//         <Box sx={{
//           display: "grid",
//           gridTemplateColumns: { 
//             xs: "repeat(2, 1fr)", 
//             sm: "repeat(3, 1fr)", 
//             md: "repeat(4, 1fr)", 
//             lg: "repeat(5, 1fr)" 
//           },
//           gap: { xs: 1.5, sm: 2 },
//           mt: 2,
//           // flexGrow: 1
//         }}>
//           {sweetProducts.map((p, i) => (
//             <Paper key={i} elevation={2} sx={{
//               borderRadius: 3,
//               overflow: "hidden",
//               border: "1px solid #f0e6ff",
//               // background: "linear-gradient(145deg, #fdfbff 0%, #f8f5ff 100%)",
//               height: { xs: 110, sm: 120, md:85, lg:85 },
//               display: "flex",
//               flexDirection: "column"
              
//             }}>
//               <Box sx={{ p: { xs: 1.5, sm: 2 }, textAlign: "center", position: "relative",  }}>
               
                
//                 <Typography variant="body2" fontWeight="bold" sx={{ 
                 
                   
//                   fontSize: { xs: "0.75rem", sm: "0.80rem", md: "0.80rem" , lg: "0.80rem"  },
//                   color: Color.primary.main,
//                   // lineHeight: 1
//                 }}>
//                   {p.name.length > 16 ? p.name.slice(0, 14) + "..." : p.name}
//                 </Typography>
                
//                 <Typography variant="caption" sx={{ 
//                   color: Color.text.secondary, 
//                   fontWeight: "bold", 
//                   fontSize: { xs: "0.75rem", sm: "0.80rem", md: "0.80rem", lg: "0.80rem", },
//                   display: "block",
                  
//                 }}>
//                   ₹{p.sales.toLocaleString()} (<Typography variant="caption" sx={{ color: "#777", fontSize: "0.70rem", pb:4 }}>
//                   {p.qty} units
//                 </Typography>)
//                 </Typography>
                
                
//               </Box>
//             </Paper>
//           ))}
//         </Box>
//       </CardContent>
//     </Card>
//   );
// };

// const ReportsDashboard = () => {
//   const [dateFilter, setDateFilter] = useState("month");
//   const [customRange, setCustomRange] = useState([
//     { startDate: safeDate(startOfMonth(new Date())), endDate: safeDate(endOfMonth(new Date())), key: "selection" }
//   ]);
//   const [search, setSearch] = useState("");
//   const [branchFilter, setBranchFilter] = useState("");
//   const [trendBranchFilter, setTrendBranchFilter] = useState("");

//   useEffect(() => {
//     setCustomRange([
//       { startDate: safeDate(startOfMonth(new Date())), endDate: safeDate(endOfMonth(new Date())), key: "selection" }
//     ]);
//   }, []);

//   const getDateRange = () => {
//     const now = safeDate(new Date());
//     let start = now, end = now;
//     switch (dateFilter) {
//       case "today": start = startOfDay(now); end = endOfDay(now); break;
//       case "week": start = startOfWeek(now, { weekStartsOn: 1 }); end = endOfWeek(now, { weekStartsOn: 1 }); break;
//       case "month": start = startOfMonth(now); end = endOfMonth(now); break;
//       case "custom": start = safeDate(customRange[0]?.startDate); end = safeDate(customRange[0]?.endDate); break;
//     }
//     return { start: safeDate(start), end: safeDate(end) };
//   };

//   const { start, end } = getDateRange();

//   const filteredOrders = useMemo(() => {
//     return rawOrders.filter(order => {
//       const orderDate = safeDate(order.order_date);
//       const inRange = isWithinInterval(orderDate, { start, end });
//       const matchesSearch = order.customer_name.toLowerCase().includes(search.toLowerCase()) ||
//         order.order_number.toLowerCase().includes(search.toLowerCase());
//       const matchesBranch = !branchFilter || order.branch_name === branchFilter;
//       return inRange && matchesSearch && matchesBranch;
//     });
//   }, [start, end, search, branchFilter]);

//   const summary = useMemo(() => {
//     const total_sales = filteredOrders.reduce((sum, o) => sum + o.total_amount, 0);
//     const total_orders = filteredOrders.length;
//     const pending = filteredOrders.filter(o => o.status !== "completed").length;
//     const completed = filteredOrders.filter(o => o.status === "completed").length;
//     return { total_sales, total_orders, pending, completed };
//   }, [filteredOrders]);

//   const topProducts = useMemo(() => {
//     const map = {};
//     filteredOrders.forEach(order => {
//       order.items.forEach(item => {
//         if (!map[item.name]) map[item.name] = { name: item.name, qty: 0, sales: 0 };
//         map[item.name].qty += item.qty;
//         map[item.name].sales += item.qty * item.price;
//       });
//     });
//     return Object.values(map);
//   }, [filteredOrders]);

//   const branchData = useMemo(() => {
//     const salesMap = {}; const ordersMap = {};
//     branches.forEach(b => { salesMap[b] = 0; ordersMap[b] = 0; });
//     filteredOrders.forEach(o => {
//       salesMap[o.branch_name] += o.total_amount;
//       ordersMap[o.branch_name] += 1;
//     });
//     const totalSales = Object.values(salesMap).reduce((a, b) => a + b, 0);
//     const totalOrders = Object.values(ordersMap).reduce((a, b) => a + b, 0);
//     return {
//       sales: Object.entries(salesMap).map(([name, value]) => ({
//         name, value: totalSales > 0 ? Number((value / totalSales * 100).toFixed(1)) : 0, amount: value
//       })),
//       orders: Object.entries(ordersMap).map(([name, value]) => ({
//         name, value: totalOrders > 0 ? Number((value / totalOrders * 100).toFixed(1)) : 0, count: value
//       }))
//     };
//   }, [filteredOrders]);

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <Box sx={{ p: { xs: 2, }, minHeight: "100vh" }}>
//         {/* HEADER */}
//         <Paper elevation={12} sx={{ borderRadius: 4, p: 2, mb: 4, bgcolor: "#1a1a2e", color: "white" }}>
//           <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
//             <Box sx={{ flex: "1 1 300px" }}>
//               <TextField fullWidth size="small" placeholder="Search customer / order..." value={search} onChange={(e) => setSearch(e.target.value)}
//                 InputProps={{
//                   startAdornment: <SearchIcon sx={{ mr: 1 }} />,
//                   endAdornment: search && <IconButton size="small" onClick={() => setSearch("")}><CloseIcon /></IconButton>,
//                 }}
//                 sx={{ bgcolor: "white", borderRadius: 2 }}
//               />
//             </Box>
//             <FormControl size="small" sx={{ minWidth: 140 }}>
//               <Select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} sx={{ bgcolor: "white" }}>
//                 <MenuItem value="today">Today</MenuItem>
//                 <MenuItem value="week">This Week</MenuItem>
//                 <MenuItem value="month">This Month</MenuItem>
//                 <MenuItem value="custom">Custom Range</MenuItem>
//               </Select>
//             </FormControl>
//             <FormControl size="small" sx={{ minWidth: 160 }}>
//               <Select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} sx={{ bgcolor: "white" }}>
//                 <MenuItem value="">All Branches</MenuItem>
//                 {branches.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
//               </Select>
//             </FormControl>
//             <Button variant="contained" startIcon={<DownloadIcon />} sx={{ backgroundColor:Color.primary.main, borderRadius: 3 }}>
//               Export Report
//             </Button>
//           </Box>

//           {dateFilter === "custom" && (
//             <Box mt={3} sx={{ bgcolor: "white", borderRadius: 3, p: 2, overflow: "hidden" }}>
//               <DateRangePicker
//                 ranges={customRange}
//                 onChange={(item) => setCustomRange([item.selection])}
//                 moveRangeOnFirstSelection={false}
//                 months={2}
//                 direction="horizontal"
//               />
//               <Box textAlign="right" mt={1}>
//                 <Button onClick={() => setDateFilter("month")} variant="outlined" size="small">Close</Button>
//               </Box>
//             </Box>
//           )}
//         </Paper>

//         {/* KPI CARDS */}
//         <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mb: 4 }}>
//           {[
//             { title: "Total Sweet Sales", value: summary.total_sales, icon: <CurrencyRupeeIcon />, prefix: "₹", gradient: "linear-gradient(135deg, #7C0BCC 0%, #4B0082 100%)" },
//             { title: "Total Orders", value: summary.total_orders, icon: <ShoppingCartIcon />, gradient: "linear-gradient(135deg, #E91E63 0%, #880E4F 100%)" },
//             { title: "Pending Orders", value: summary.pending, icon: <PendingIcon />, gradient: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)" },
//             { title: "Completed", value: summary.completed, icon: <CheckCircleIcon />, gradient: "linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)" },
//           ].map((card, i) => (
//             <MemoizedCard key={i} {...card} />
//           ))}
//         </Box>

//         {/* TOP PRODUCTS + PIE CHARTS - 50:50 SPLIT */}
//         <Grid container spacing={2} mb={4}>
//           {/* Top Products - Compact Cards */}
//           <Grid item xs={12} md={6}>
//             <TopProductsCard products={topProducts} />
//           </Grid>

//           {/* Branch Charts - Wider & Taller */}
//           <Grid item xs={12} md={6} lg={6}>
//             <Box sx={{ 
//               display: "flex", 
//               flexDirection: { xs: "column", sm: "row" }, 
//               gap: 2, 
//               height: "100%",
//               alignItems: "stretch",
//               width:{ md:410, lg:694 }
//             }}>
//               <Card sx={{ borderRadius: 4, flex: 1, minHeight: { xs: 300, sm: 340 }, width:{ md:410, lg:694 } }}>
//                 <CardContent>
//                   <Typography variant="h6" fontWeight="bold" gutterBottom>Branch Sales %</Typography>
//                   <ResponsiveContainer width="100%" height={260}>
//                     <PieChart>
//                       <Pie 
//                         data={branchData.sales} 
//                         dataKey="value" 
//                         nameKey="name" 
//                         cx="50%" 
//                         cy="50%" 
//                         outerRadius={50} 
//                         label={({ name, amount }) => `${name}: ₹${amount.toLocaleString()}`}
//                       >
//                         {branchData.sales.map((_, i) => (
//                           <Cell key={i} fill={COLORS[i % COLORS.length]} />
//                         ))}
//                       </Pie>
//                       <Tooltip formatter={(v) => `${v}%`} />
//                     </PieChart>
//                   </ResponsiveContainer>
//                 </CardContent>
//               </Card>

//               <Card sx={{ borderRadius: 4, flex: 1, minHeight: { xs: 300, sm: 340 } }}>
//                 <CardContent>
//                   <Typography variant="h6" fontWeight="bold" gutterBottom>Branch Orders %</Typography>
//                   <ResponsiveContainer width="100%" height={260}>
//                     <PieChart>
//                       <Pie 
//                         data={branchData.orders} 
//                         dataKey="value" 
//                         nameKey="name" 
//                         cx="50%" 
//                         cy="50%" 
//                         outerRadius={50} 
//                         label={({ name, count }) => `${name}: ${count}`}
//                         sx={{fontSize:10}}
//                       >
//                         {branchData.orders.map((_, i) => (
//                           <Cell key={i} fill={COLORS[i % COLORS.length]} />
//                         ))}
//                       </Pie>
//                       <Tooltip formatter={(v) => `${v}%`} />
//                     </PieChart>
//                   </ResponsiveContainer>
//                 </CardContent>
//               </Card>
//             </Box>
//           </Grid>
//         </Grid>

//         {/* TREND CHART */}
//         <Card sx={{ borderRadius: 4, mb: 4 }}>
//           <CardContent sx={{ p: 3 }}>
//             <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
//               <Typography variant="h6" fontWeight="bold" color={Color.text.primary}>
//                 Product Orders & Sales 
//               </Typography>
//               <FormControl size="small" sx={{ minWidth: 180 }}>
//                 <Select value={trendBranchFilter} onChange={(e) => setTrendBranchFilter(e.target.value)} sx={{ bgcolor: "white" }}>
//                   <MenuItem value="">All Branches</MenuItem>
//                   {branches.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
//                 </Select>
//               </FormControl>
//             </Box>
//             <ResponsiveContainer width="100%" height={380}>
//               <LineChart data={monthlySalesData}>
//                 <CartesianGrid strokeDasharray="4 4" />
//                 <XAxis dataKey="month" />
//                 <YAxis yAxisId="left" />
//                 <YAxis yAxisId="right" orientation="right" />
//                 <Tooltip formatter={(v, name) => name === "sales" ? `₹${v.toLocaleString()}` : v} />
//                 <Legend />
//                 <Line yAxisId="left" type="monotone" dataKey="sales" stroke="#7C0BCC" strokeWidth={4} name="Sales (₹)" />
//                 <Line yAxisId="right" type="monotone" dataKey="orders" stroke="#E91E63" strokeWidth={4} name="Orders" />
//               </LineChart>
//             </ResponsiveContainer>
//           </CardContent>
//         </Card>

//         {/* TABLE */}
//         <Card sx={{ borderRadius: 4 }}>
//           <CardContent>
//             <Typography variant="h6" fontWeight="bold" gutterBottom>Detailed Sweet Orders</Typography>
//             <Box sx={{ height: 650, width: "100%" }}>
//               <DataGrid
//                 rows={filteredOrders}
//                 columns={[
//                   { field: "order_number", headerName: "Order No", width: 250, headerClassName: 'custom-header' },
//                   { field: "customer_name", headerName: "Customer", width: 250, headerClassName: 'custom-header' },
//                   { field: "branch_name", headerName: "Branch", width: 250, headerClassName: 'custom-header' },
//                   { field: "order_date", headerName: "Date", width: 250, headerClassName: 'custom-header',
//                     valueFormatter: (p) => format(safeDate(p.value), "dd MMM yyyy") },
//                   { field: "total_amount", headerName: "Amount", width: 250, headerClassName: 'custom-header',
//                     renderCell: (p) => `₹${p.value.toLocaleString()}` },
//                   { field: "status", headerName: "Status", width: 250, headerClassName: 'custom-header' },
//                 ]}
//                 pageSizeOptions={[10, 25, 50, 100]}
//                 sx={{
//                   "& .MuiDataGrid-columnHeaders": {
//                     bgcolor: "#7C0BCC",
//                     color: "white",
//                     fontWeight: "bold"
//                   },
//                   "& .custom-header .MuiDataGrid-columnHeaderTitle": {
//                     color: "#000 !important",
//                     fontWeight: "bold"
//                   }
//                 }}
//               />
//             </Box>
//           </CardContent>
//         </Card>
//       </Box>
//     </LocalizationProvider>
//   );
// };

// export default ReportsDashboard;